import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Modal,
  Button,
  Form,
  Spinner,
} from "react-bootstrap";
import { getCountries } from "../services/APIClient";
import "./BannerModal.css";

function BannerModal({ isOpen, onClose, banner, onSave, isSaving }) {
  const [formData, setFormData] = useState({
    banner_type: "",
    header: "",
    locale: "en-IE",
    is_active: true,
  });

  const [errors, setErrors] = useState({});
  const [countries, setCountries] = useState([]);
  const [countriesLoading, setCountriesLoading] = useState(false);

  useEffect(() => {
    if (banner) {
      setFormData({
        ...banner,
        content: banner.content || "",
        locale: banner.locale || "en-IE",
        is_active: banner.is_active !== undefined ? banner.is_active : true,
      });
    } else {
      setFormData({
        banner_type: "",
        header: "",
        locale: "en-IE",
        is_active: true,
      });
    }
    setErrors({});

    // Load countries when modal opens
    const loadCountries = async () => {
      setCountriesLoading(true);
      try {
        const response = await getCountries();
        const countryList = response.data || response;
        setCountries(Array.isArray(countryList) ? countryList : []);
      } catch (err) {
        console.error("Error loading countries:", err);
        setCountries([]);
      } finally {
        setCountriesLoading(false);
      }
    };

    if (isOpen) {
      loadCountries();
    }
  }, [banner, isOpen]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const finalValue = type === "checkbox" ? checked : value;

    setFormData((prev) => ({
      ...prev,
      [name]: finalValue,
    }));

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.banner_type.trim()) {
      newErrors.banner_type = "Banner type is required";
    }
    if (!formData.header.trim()) {
      newErrors.header = "Header is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      await onSave(formData);
    } catch (error) {
      console.error("Error saving banner:", error);
    }
  };

  return (
    <Modal show={isOpen} onHide={onClose} size="lg" className="banner-modal">
      <Modal.Header closeButton>
        <Modal.Title>
          {banner?.id ? "Edit Banner" : "Create New Banner"}
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body className="banner-modal-body">
          <Form.Group className="mb-3">
            <Form.Label>
              Banner Type <span className="required">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              name="banner_type"
              placeholder="e.g., freeTrialBanner_trialActive"
              value={formData.banner_type}
              onChange={handleInputChange}
              disabled={!!banner?.id}
              isInvalid={!!errors.banner_type}
              className="banner-input"
            />
            {errors.banner_type && (
              <Form.Control.Feedback type="invalid" className="d-block">
                {errors.banner_type}
              </Form.Control.Feedback>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              Country <span className="required">*</span>
            </Form.Label>
            <Form.Select
              name="countryid"
              value={formData.countryid || ""}
              onChange={handleInputChange}
              disabled={countriesLoading}
              className="banner-input"
              required
            >
              <option value="">Select a country...</option>
              {countries.map((country) => (
                <option key={country.id} value={country.id}>
                  {country.name} ({country.id})
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Locale</Form.Label>
            <Form.Select
              name="locale"
              value={formData.locale || "en-IE"}
              onChange={handleInputChange}
              className="banner-input"
            >
              <option value="en-IE">English (Ireland)</option>
              <option value="en-US">English (US)</option>
              <option value="en-GB">English (UK)</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              Header <span className="required">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              name="header"
              placeholder="Main heading text"
              value={formData.header}
              onChange={handleInputChange}
              isInvalid={!!errors.header}
              className="banner-input"
            />
            {errors.header && (
              <Form.Control.Feedback type="invalid" className="d-block">
                {errors.header}
              </Form.Control.Feedback>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Subheader (Optional)</Form.Label>
            <Form.Control
              type="text"
              name="subheader"
              placeholder="Secondary heading"
              value={formData.subheader || ""}
              onChange={handleInputChange}
              className="banner-input"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Content (JSON array of paragraphs)</Form.Label>
            <Form.Control
              as="textarea"
              name="content"
              placeholder='["First paragraph text", "Second paragraph text", "Third paragraph text"]'
              value={formData.content || ""}
              onChange={handleInputChange}
              rows={6}
              className="banner-input"
            />
            <Form.Text className="text-muted">
              Enter paragraphs as a JSON array of strings. Example: ["Paragraph 1", "Paragraph 2"]
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Link URL (Optional)</Form.Label>
            <Form.Control
              type="url"
              name="link_url"
              placeholder="https://example.com"
              value={formData.link_url || ""}
              onChange={handleInputChange}
              className="banner-input"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Accept Button Label (Optional)</Form.Label>
            <Form.Control
              type="text"
              name="button_accept_label"
              placeholder="e.g., Purchase Now"
              value={formData.button_accept_label || ""}
              onChange={handleInputChange}
              className="banner-input"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Cancel Button Label (Optional)</Form.Label>
            <Form.Control
              type="text"
              name="button_cancel_label"
              placeholder="e.g., Close"
              value={formData.button_cancel_label || ""}
              onChange={handleInputChange}
              className="banner-input"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              name="is_active"
              id="is_active"
              label="Active"
              checked={formData.is_active || false}
              onChange={handleInputChange}
            />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer className="banner-modal-footer">
          <Button variant="secondary" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={isSaving}>
            {isSaving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" /> Saving...
              </>
            ) : (
              "Save Banner"
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

BannerModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  banner: PropTypes.shape({
    id: PropTypes.string,
    banner_type: PropTypes.string,
    countryid: PropTypes.string,
    header: PropTypes.string,
    subheader: PropTypes.string,
    content: PropTypes.string,
    link_url: PropTypes.string,
    button_accept_label: PropTypes.string,
    button_cancel_label: PropTypes.string,
    locale: PropTypes.string,
    is_active: PropTypes.bool,
  }),
  onSave: PropTypes.func.isRequired,
  isSaving: PropTypes.bool.isRequired,
};

export default BannerModal;